import { upperFirst } from 'lodash'
import {
  FinancialAccountPrimaryUse,
  FinancialType,
  type FinancialAccount,
} from '@/reducers/finances/financialAccountsReducer'
import { buildDesignationUpdate } from '@/features/onboarding/accounts-stage/designation'
import { isPrimaryUseAnswerComplete } from '@/features/onboarding/connect-stage/primaryUseAnswers'
import type { PrimaryUseAnswer } from '@/features/onboarding/connect-stage/types'
import { plural } from '../../lib/plural'
import type { WidgetDisplay } from '../../lib/types'

export type AccountUsageAnswers = Record<number, PrimaryUseAnswer>

export const FOLLOW_UP_QUESTIONS: Record<FinancialAccountPrimaryUse, string> = {
  [FinancialAccountPrimaryUse.BUSINESS]:
    'Do you ever pay for anything personal on it, like groceries or a family dinner?',
  [FinancialAccountPrimaryUse.PERSONAL]:
    'Any business costs paid from it, like a license or supplies?',
}

/** The accounts the API's `needsPrimaryUse` scope counts, so the card can clear it. */
export const isUsageAccount = (account: FinancialAccount): boolean =>
  !account.inactive &&
  account.isHidden !== true &&
  account.accountType !== FinancialType.USER_MANUAL_UPLOAD

export const describeAccount = (account: FinancialAccount): string =>
  [
    account.subtype ? upperFirst(account.subtype) : null,
    account.mask ? `•${account.mask}` : null,
  ]
    .filter(Boolean)
    .join(' ')

export const isEveryAnswerComplete = (
  accountIds: number[],
  answers: AccountUsageAnswers
): boolean =>
  accountIds.length > 0 &&
  accountIds.every((id) => isPrimaryUseAnswerComplete(answers[id]))

/** The same payload the onboarding connect stage saves. */
export const toUsageUpdate = (answer: PrimaryUseAnswer) => {
  const isBusinessAccount =
    answer.primaryUse === FinancialAccountPrimaryUse.BUSINESS
  const answeredFollowUp = answer.hasOtherType ?? undefined
  const followUp = isBusinessAccount
    ? { hasPersonalTransactions: answeredFollowUp }
    : { hasBusinessTransactions: answeredFollowUp }
  return {
    onboardingAnswers: { isBusinessAccount, ...followUp },
    ...buildDesignationUpdate({ isBusinessAccount, ...followUp }),
  }
}

const countOf = (answers: PrimaryUseAnswer[]) => ({
  business: answers.filter(
    (answer) => answer.primaryUse === FinancialAccountPrimaryUse.BUSINESS
  ).length,
  personal: answers.filter(
    (answer) => answer.primaryUse === FinancialAccountPrimaryUse.PERSONAL
  ).length,
  mixed: answers.filter((answer) => answer.hasOtherType === true).length,
})

export const toUsageCount = (answers: PrimaryUseAnswer[]): string => {
  const { business, personal } = countOf(answers)
  return `${business} business · ${personal} personal`
}

export const toUsageOutcome = (
  answers: PrimaryUseAnswer[]
): { text: string; display: WidgetDisplay } => {
  const { business, personal, mixed } = countOf(answers)
  const mixedText =
    mixed === 0
      ? 'None carries the other kind of spending.'
      : `${plural(mixed, 'account')} also ${mixed === 1 ? 'carries' : 'carry'} the other kind of spending.`
  return {
    text: `The member said how they use every account: ${business} business, ${personal} personal. ${mixedText}`,
    display: {
      label: `${business} business, ${personal} personal`,
      ...(mixed === 0 ? {} : { meta: `${mixed} with both kinds of spending` }),
    },
  }
}
